import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { ChainId } from 'symbiosis-js-sdk'
import { EthereumProvider } from '@walletconnect/ethereum-provider'

import { ChainConfig } from '~/types/config'

import {
	Action,
	WalletHook,
	WalletMethods,
	WalletState,
	WalletStatus,
	WalletType,
} from './types'
import { getProvider } from './utils'

const walletStorage = {
	connected: (walletType: WalletType) =>
		localStorage.setItem('wallet', walletType.toString()),
	disconnected: () => localStorage.removeItem('wallet'),
	walletType: (): WalletType => {
		const storeWalletType = Number.parseInt(
			localStorage.getItem('wallet'),
			10
		)
		return (
			(Object.values(WalletType).find(
				(walletType) => walletType == storeWalletType
			) as WalletType) || WalletType.NONE
		)
	},
}

const WalletContext = createContext<[WalletState, WalletMethods]>(null)

const initializedWalletState: WalletState = {
	status: WalletStatus.DISCONNECTED,
	web3: null,
	account: null,
	chainId: null,
	walletType: WalletType.NONE,
}

const walletReducer = (state: WalletState, action: Action): WalletState => {
	switch (action.type) {
		case 'connect':
			walletStorage.connected(action.walletType)
			return {
				...state,
				account: action.account,
				chainId: action.chainId,
				status: WalletStatus.CONNECTED,
				web3: action.web3,
				walletType: action.walletType,
			}
		case 'disconnect':
			walletStorage.disconnected()
			return initializedWalletState
		case 'account': {
			return {
				...state,
				account: action.account,
				status: !!action.account
					? WalletStatus.CONNECTED
					: WalletStatus.DISCONNECTED,
			}
		}
		case 'chainChanged':
			return state.chainId == action.chainId
				? state
				: {
						...state,
						chainId: action.chainId,
				  }
		case 'connecting':
			return { ...state, status: WalletStatus.CONNECTING }
	}
	return state
}

export const WalletProvider: React.FC = (props) => {
	let [walletState, dispatch] = useReducer(
		walletReducer,
		initializedWalletState
	)

	const subscribe = (provider) => {
		provider.on('chainChanged', (chainId) =>
			dispatch({
				type: 'chainChanged',
				chainId: Number.parseInt(chainId, 16),
			})
		)

		provider.on('accountsChanged', (accounts) => {
			let account = accounts.length > 0 ? accounts[0] : null
			if (!account) {
				unsubscribe(provider)
				dispatch({ type: 'disconnect' })
			} else {
				if (walletState.account != account)
					dispatch({
						type: 'account',
						account: accounts.length > 0 ? accounts[0] : null,
					})
			}
		})

		provider.on('disconnect', () => {
			unsubscribe(provider)
			dispatch({ type: 'disconnect' })
		})
	}

	const unsubscribe = (provider) => {
		if (provider instanceof EthereumProvider) {
			provider.disconnect()
			return
		}

		provider.removeAllListeners()
	}

	const connect = (walletType: WalletType): Promise<void> => {
		dispatch({ type: 'connecting' })
		return getProvider(walletType)
			.then((web3) => {
				return Promise.all([
					web3.provider.request({ method: 'eth_requestAccounts' }),
					web3.provider.request({ method: 'eth_chainId' }),
				]).then(([accounts, chainId]) => {
					subscribe(web3.provider)
					dispatch({
						type: 'connect',
						chainId: Number.parseInt(chainId, 16),
						account: accounts.length > 0 ? accounts[0] : null,
						web3,
						walletType,
					})
				})
			})
			.catch((error) => {
				console.error(error)
				dispatch({ type: 'disconnect' })
			})
	}

	const disconnect = (): Promise<void> => {
		if (walletState.status != WalletStatus.CONNECTED)
			return Promise.reject('not connected')
		unsubscribe(walletState.web3.provider)
		dispatch({ type: 'disconnect' })
	}

	const setChain = (chainId: ChainId): Promise<void> => {
		return walletState.web3.provider.request({
			method: 'wallet_switchEthereumChain',
			params: [{ chainId: '0x' + chainId.toString(16) }],
		})
	}

	const isCurrentChain = (chainId: ChainId) => walletState.chainId === chainId

	const addChain = (chainConfig: ChainConfig): Promise<void> =>
		walletState.web3.provider.request({
			method: 'wallet_addEthereumChain',
			params: [
				{
					chainId: '0x' + chainConfig.id.toString(16),
					chainName: chainConfig.name,
					rpcUrls: [chainConfig.rpc],
					blockExplorerUrls: [chainConfig.scanUrl],
					nativeCurrency: {
						symbol: chainConfig.token.symbol,
						decimals: chainConfig.token.decimals,
					},
				},
			],
		})

	const canSetChain = (): boolean =>
		[WalletType.METAMASK].includes(walletState.walletType)

	useEffect(() => {
		const walletType = walletStorage.walletType()
		if (walletType != WalletType.NONE) {
			connect(walletType).catch(console.error)
			return () => {
				disconnect().catch(console.error)
			}
		}
	}, [])

	return (
		<WalletContext.Provider
			value={[
				walletState,
				{
					connect,
					disconnect,
					setChain,
					addChain,
					isCurrentChain,
					canSetChain,
				},
			]}
			children={props.children}
		/>
	)
}

export const useWallet = (): WalletHook => {
	const [
		state,
		{
			disconnect,
			connect,
			setChain,
			addChain,
			isCurrentChain,
			canSetChain,
		},
	] = useContext(WalletContext)
	return {
		...state,
		connect,
		disconnect,
		setChain,
		addChain,
		isCurrentChain,
		canSetChain,
	}
}
